import type { CardComponentProps } from 'nextstepjs';
import { useNextStep } from 'nextstepjs';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { faInfoCircle, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Collapse, Stack, Typography, useTheme } from '@mui/material';

import useAlertTraining, { AlertTours, CustomStep } from 'src/hooks/useAlertTraining';

function TrainingToolTip(props: CardComponentProps) {
    const { step, currentStep, totalSteps, nextStep = () => {}, arrow } = props;

    const { createTrainingAlert, removeTrainingAlert } = useAlertTraining();

    const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
    const { palette, spacing } = useTheme();
    const showControls = step?.showControls;
    const navigate = useNavigate();
    const { closeNextStep, currentTour } = useNextStep();
    const isLastStep = currentStep === totalSteps - 1;

    const showCompletionControls = useMemo(() => {
        const currentTourObj = currentTour
            ? AlertTours.find((tour) => tour.tour === currentTour)
            : undefined;
        if (!currentTourObj) {
            return false;
        }
        const currentStepObj = currentTourObj.steps[currentStep] as CustomStep | undefined;
        return currentStepObj?.showCompletionControls ?? false;
    }, [currentTour, currentStep]);

    const handlePrimaryClick = () => {
        if (isLastStep) {
            closeNextStep();
        } else {
            nextStep();
        }
    };

    const handleRetryClick = () => {
        closeNextStep();
        createTrainingAlert(true);
        navigate('/alerts');
    };

    const handleCloseClick = () => {
        setShowCloseConfirmation(true);
    };

    const handleEndTourClick = () => {
        closeNextStep();
        removeTrainingAlert();
        navigate('/dashboard');
    };

    const handleContinueClick = () => {
        setShowCloseConfirmation(false);
    };

    const handleEndTransition = () => {
        const timeout = setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
        }, 200);
        return () => clearTimeout(timeout);
    };

    return (
        <Box
            sx={{
                padding: 2,
                background: palette?.cyan?.[100],
                borderRadius: 3,
                width: `calc(100vw - ${spacing(4.5)})`,
                maxWidth: '500px',
            }}
        >
            {arrow}
            {/* End Tour Confirmation */}
            <Collapse in={showCloseConfirmation} addEndListener={handleEndTransition}>
                <Stack sx={{ gap: 2 }}>
                    <Typography variant="body2" sx={{ color: 'cyan.900' }}>
                        Are you sure you want to end the tour?
                    </Typography>
                    <Stack direction="row" sx={{ gap: 1 }}>
                        <Button
                            fullWidth
                            variant="outlined"
                            size="small"
                            onClick={handleContinueClick}
                            sx={{
                                borderColor: palette?.cyan?.[900],
                                borderWidth: 1,
                                color: palette?.cyan?.[900],
                            }}
                        >
                            Continue Tour
                        </Button>
                        <Button
                            fullWidth
                            variant="contained"
                            size="small"
                            onClick={handleEndTourClick}
                            sx={{
                                background: palette?.cyan?.[300],
                                color: palette?.cyan?.[900],
                            }}
                        >
                            End Tour
                        </Button>
                    </Stack>
                </Stack>
            </Collapse>
            <Collapse in={!showCloseConfirmation}>
                <Stack direction="column" sx={{ gap: 2 }}>
                    <Stack direction="row" sx={{ gap: 2, alignItems: 'center' }}>
                        {/* Icon */}
                        <Box
                            sx={{
                                background: palette.cyan?.[300],
                                padding: 1,
                                borderRadius: 2,
                                lineHeight: 0,
                                height: 'fit-content',
                                alignSelf: 'flex-start',
                            }}
                        >
                            <FontAwesomeIcon
                                icon={faInfoCircle}
                                color={palette?.cyan?.[900]}
                                size="sm"
                            />
                        </Box>

                        {/* Title & Body */}
                        <Stack sx={{ flexGrow: 1 }}>
                            {step?.title && (
                                <Typography
                                    variant="h4"
                                    sx={{ fontWeight: 'bold', color: palette?.cyan?.[900] }}
                                >
                                    {step.title}
                                </Typography>
                            )}

                            <Typography variant="body2" sx={{ color: palette?.cyan?.[900] }}>
                                {step?.content}
                            </Typography>
                        </Stack>

                        {/* Close Button */}
                        <Button
                            sx={{
                                padding: 0.5,
                                paddingTop: 0,
                                minWidth: 'fit-content',
                                alignSelf: 'flex-start',
                            }}
                            onClick={handleCloseClick}
                        >
                            <FontAwesomeIcon
                                icon={faXmark}
                                color={palette?.cyan?.[900]}
                                size="lg"
                            />
                        </Button>
                    </Stack>

                    {/* Footer */}
                    {showControls &&
                        (showCompletionControls ? (
                            <Stack sx={{ gap: 1.5 }}>
                                <Button
                                    aria-label="Complete"
                                    role="button"
                                    onClick={handleEndTourClick}
                                    variant="contained"
                                    size="small"
                                    sx={{
                                        background: palette?.cyan?.[500],
                                        color: palette?.common?.white,
                                    }}
                                >
                                    Complete
                                </Button>
                                <Button
                                    aria-label="Retry"
                                    role="button"
                                    onClick={handleRetryClick}
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        borderColor: palette?.cyan?.[900],
                                        borderWidth: 1,
                                        color: palette?.cyan?.[900],
                                    }}
                                >
                                    Retry
                                </Button>
                            </Stack>
                        ) : (
                            <Button
                                aria-label="Next"
                                role="button"
                                onClick={handlePrimaryClick}
                                variant="contained"
                                size="small"
                                sx={{
                                    background: palette?.cyan?.[500],
                                    color: palette?.common?.white,
                                }}
                            >
                                Next
                            </Button>
                        ))}
                </Stack>
            </Collapse>
        </Box>
    );
}

export default TrainingToolTip;
